import { useEffect, useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { Download, Search } from "lucide-react";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { NativeSelect, NativeSelectOption } from "@components/ui/native-select";
import { LeadsTable } from "@components/leads/LeadsTable";
import { LeadsPagination } from "@components/leads/LeadsPagination";
import { EmptyLeads } from "@components/leads/EmptyLeads";
import type { InertiaProps } from "@shared";

export type AccountLeadsProps =
  InertiaProps.paths["/leads"]["get"]["responses"]["200"]["content"]["application/json"];

type FilterParams = {
  website_id?: string;
  page_id?: string;
  q?: string;
  page?: string;
};

const buildQuery = (params: FilterParams): Record<string, string> => {
  const out: Record<string, string> = {};
  if (params.website_id) out.website_id = params.website_id;
  if (params.page_id) out.page_id = params.page_id;
  if (params.q) out.q = params.q;
  if (params.page && params.page !== "1") out.page = params.page;
  return out;
};

const buildExportUrl = (params: FilterParams): string => {
  const qs = new URLSearchParams(buildQuery(params)).toString();
  return qs ? `/leads/export?${qs}` : "/leads/export";
};

export default function AccountLeads() {
  const { leads, websites, pages, workspace_custom_fields, pagination, filters } =
    usePage<AccountLeadsProps>().props;

  const hasLeads = leads.length > 0;
  const websiteFilter = filters.website_id ? String(filters.website_id) : "";
  const pageFilter = filters.page_id ? String(filters.page_id) : "";

  // Local state for the search input so typing feels instant; we debounce
  // submission to the server via a 300ms timer.
  const [searchInput, setSearchInput] = useState(filters.q ?? "");
  useEffect(() => setSearchInput(filters.q ?? ""), [filters.q]);

  const visit = (params: FilterParams) => {
    router.get("/leads", buildQuery(params), {
      preserveScroll: true,
      preserveState: true,
    });
  };

  useEffect(() => {
    const current = filters.q ?? "";
    if (searchInput === current) return;
    const handle = setTimeout(() => {
      visit({ website_id: websiteFilter, page_id: pageFilter, q: searchInput });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleWebsiteChange = (value: string) => {
    // Clearing a website filter also clears the page filter, since pages
    // are website-scoped and a page_id without its website is meaningless.
    visit({ website_id: value, page_id: undefined, q: filters.q ?? undefined });
  };

  const handlePageChange = (value: string) => {
    visit({ website_id: websiteFilter, page_id: value, q: filters.q ?? undefined });
  };

  const visiblePages = websiteFilter
    ? pages.filter((p) => String(p.website_id) === websiteFilter)
    : pages;

  const exportUrl = buildExportUrl({
    website_id: websiteFilter,
    page_id: pageFilter,
    q: filters.q ?? undefined,
  });

  return (
    <main className="mx-auto container max-w-6xl px-8 py-8">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-base-600">Customer Leads</h1>
          <p className="text-base-500 text-sm mt-1">
            {pagination.total_count} {pagination.total_count === 1 ? "lead" : "leads"} across all
            websites
          </p>
        </div>
        <a
          href={exportUrl}
          download
          className={hasLeads ? "" : "pointer-events-none"}
          data-testid="export-csv-link"
        >
          <Button variant="default" size="sm" disabled={!hasLeads} className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </a>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="leads-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-base-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, or phone…"
            className="pl-9"
            data-testid="leads-search"
          />
        </div>

        <NativeSelect
          value={websiteFilter}
          onChange={(e) => handleWebsiteChange(e.target.value)}
          data-testid="website-filter"
        >
          <NativeSelectOption value="">All websites</NativeSelectOption>
          {websites.map((w) => (
            <NativeSelectOption key={w.id} value={String(w.id)}>
              {w.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>

        <NativeSelect
          value={pageFilter}
          onChange={(e) => handlePageChange(e.target.value)}
          disabled={!websiteFilter}
          data-testid="page-filter"
        >
          <NativeSelectOption value="">All pages</NativeSelectOption>
          {visiblePages.map((p) => (
            <NativeSelectOption key={p.id} value={String(p.id)}>
              {p.title}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>

      <div className="border border-neutral-300 bg-white rounded-lg overflow-hidden">
        <LeadsTable
          leads={leads}
          workspaceCustomFields={workspace_custom_fields}
          showWebsite
          showPage
        />
        {!hasLeads && <EmptyLeads />}
      </div>

      <LeadsPagination
        pageUrl={(p) =>
          `/leads?${new URLSearchParams(
            buildQuery({
              website_id: websiteFilter,
              page_id: pageFilter,
              q: filters.q ?? undefined,
              page: String(p),
            })
          ).toString()}`
        }
        pagination={pagination}
        disabled={!hasLeads}
      />
    </main>
  );
}
